class VariantDropdown extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    this.trigger = this.querySelector('[data-variant-dropdown-trigger]');
    this.value = this.querySelector('[data-variant-dropdown-value]');
    this.menu = this.querySelector('[data-variant-dropdown-menu]');
    this.optionButtons = Array.from(this.querySelectorAll('[data-variant-dropdown-option]'));

    if (!this.select || !this.trigger || !this.value || !this.menu) return;

    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleTriggerKeydown = this.handleTriggerKeydown.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.handleMenuKeydown = this.handleMenuKeydown.bind(this);
    this.handleSelectChange = this.handleSelectChange.bind(this);
    this.handleOptionClick = this.handleOptionClick.bind(this);

    this.trigger.addEventListener('click', this.handleTriggerClick);
    this.trigger.addEventListener('keydown', this.handleTriggerKeydown);
    this.select.addEventListener('change', this.handleSelectChange);
    this.menu.addEventListener('click', this.handleOptionClick);
    this.menu.addEventListener('keydown', this.handleMenuKeydown);

    this.observer = new MutationObserver(() => this.syncFromSelect());
    this.observer.observe(this.select, {
      attributes: true,
      subtree: true,
      childList: true,
      attributeFilter: ['disabled', 'selected'],
    });

    this.syncFromSelect(false);
    this.hasInitialized = true;
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.handleTriggerClick);
    this.trigger?.removeEventListener('keydown', this.handleTriggerKeydown);
    this.select?.removeEventListener('change', this.handleSelectChange);
    this.menu?.removeEventListener('click', this.handleOptionClick);
    this.menu?.removeEventListener('keydown', this.handleMenuKeydown);
    this.observer?.disconnect();
    this.removeDocumentListeners();
  }

  handleTriggerClick() {
    this.toggle(!this.isOpen(), true);
  }

  handleTriggerKeydown(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();
    this.toggle(true, false);
    const options = this.getEnabledOptions();
    this.focusOption(event.key === 'ArrowUp' ? options.at(-1) : options[0]);
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.toggle(false);
    }
  }

  handleDocumentKeydown(event) {
    if (event.key === 'Escape') {
      this.toggle(false);
      this.trigger?.focus();
    }
  }

  handleSelectChange() {
    this.syncFromSelect();
    this.toggle(false);
  }

  handleOptionClick(event) {
    const optionButton = event.target.closest('[data-variant-dropdown-option]');

    if (!(optionButton instanceof HTMLButtonElement) || optionButton.disabled || !this.select) return;

    const { value } = optionButton.dataset;
    const targetOption = Array.from(this.select.options).find((option) => option.value === value);

    if (!targetOption) return;

    this.select.value = targetOption.value;
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.trigger?.focus();
  }

  handleMenuKeydown(event) {
    const options = this.getEnabledOptions();
    if (options.length === 0) return;

    const currentIndex = Math.max(0, options.indexOf(document.activeElement));
    let target;

    switch (event.key) {
      case 'ArrowDown':
        target = options[(currentIndex + 1) % options.length];
        break;
      case 'ArrowUp':
        target = options[(currentIndex - 1 + options.length) % options.length];
        break;
      case 'Home':
        target = options[0];
        break;
      case 'End':
        target = options.at(-1);
        break;
      case 'Tab':
        this.toggle(false);
        return;
      default:
        return;
    }

    event.preventDefault();
    this.focusOption(target);
  }

  isOpen() {
    return this.dataset.open === 'true';
  }

  toggle(forceOpen, moveFocus = false) {
    const shouldOpen = typeof forceOpen === 'boolean' ? forceOpen : !this.isOpen();

    this.dataset.open = shouldOpen ? 'true' : 'false';
    this.trigger?.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    this.menu.hidden = !shouldOpen;

    if (shouldOpen) {
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleDocumentKeydown);
      if (moveFocus) {
        requestAnimationFrame(() => {
          const selectedOption =
            this.optionButtons.find((option) => option.getAttribute('aria-selected') === 'true' && !option.disabled) ||
            this.getEnabledOptions()[0];
          this.focusOption(selectedOption);
        });
      }
    } else {
      this.optionButtons.forEach((option) => {
        option.tabIndex = -1;
      });
      this.removeDocumentListeners();
    }
  }

  getEnabledOptions() {
    return this.optionButtons.filter((option) => !option.disabled);
  }

  focusOption(option) {
    if (!(option instanceof HTMLButtonElement)) return;

    this.optionButtons.forEach((button) => {
      button.tabIndex = button === option ? 0 : -1;
    });
    option.focus();
  }

  removeDocumentListeners() {
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
  }

  syncFromSelect(announce = true) {
    if (!this.select || !this.value) return;

    const selectedOption = this.select.options[this.select.selectedIndex];

    if (!selectedOption) return;

    const label = selectedOption.dataset.optionLabel || selectedOption.textContent?.trim() || '';
    this.value.textContent = label;

    for (const optionButton of this.optionButtons) {
      const matchingOption = Array.from(this.select.options).find(
        (option) => option.value === optionButton.dataset.value,
      );
      const isSelected = optionButton.dataset.value === selectedOption.value;
      optionButton.dataset.selected = isSelected ? 'true' : 'false';
      optionButton.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      optionButton.disabled = Boolean(matchingOption?.disabled);
      optionButton.tabIndex = -1;
    }

    if (announce && this.hasInitialized) {
      const status = this.querySelector('[data-variant-dropdown-status]');
      if (status) {
        // Prefer a human label. Add-on selects are named
        // `addon-group-<id>-<kind>-<id>-option-<n>`, which the old `options[...]`
        // strip left completely intact and unreadable.
        const labelSource =
          this.select.getAttribute('aria-label') ||
          (this.select.id ? document.querySelector(`label[for="${this.select.id}"]`)?.textContent : '') ||
          this.select.name.replace(/^options\[|\]$/g, '');
        const optionName = (labelSource || '').replace(/\s*:\s*$/, '').trim();
        status.textContent = optionName
          ? `${optionName}: ${selectedOption.textContent?.trim() || label}`
          : `${selectedOption.textContent?.trim() || label}`;
      }
    }
  }
}

if (!customElements.get('variant-dropdown')) {
  customElements.define('variant-dropdown', VariantDropdown);
}
